import React from "react";
import { Clock } from "lucide-react";
import ListPersonAvatar, { resolveRowProfileImageUrl } from "./ListPersonAvatar";
import {
  formatCellDisplay,
  getPriorityPillTone,
  getStatusPillTone,
  isNameAvatarColumn,
  isPriorityColumnKey,
  isStatusColumnKey,
} from "./listCellHelpers";

const SUBTITLE_RULES = [
  /(designation|job_title|position|program|specialization)/i,
  /(department|role_name|course|batch|unit_name|employment_type)/i,
];

const IMAGE_KEY = /(profile_image|photo|avatar|image_url)/i;

const TAG_KEY = /(^|_)(tags?|labels?)($|_)/i;

const TIME_KEYS = [
  "updated_at",
  "last_activity_at",
  "last_activity",
  "created_at",
];

const NAME_PART_GROUPS = [
  ["first_name", "middle_name", "last_name"],
  ["person.first_name", "person.middle_name", "person.last_name"],
  ["org_persons.person.first_name", "org_persons.person.middle_name", "org_persons.person.last_name"],
  ["organization_person.person.first_name", "organization_person.person.middle_name", "organization_person.person.last_name"],
];

function plainText(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") return "";
  const text = String(value).trim();
  if (!text || text === "-" || text === "—" || text === "null") return "";
  return text;
}

function isNamePartKey(key) {
  const lower = String(key || "").toLowerCase();
  return (
    lower === "first_name" ||
    lower === "middle_name" ||
    lower === "last_name" ||
    lower.endsWith(".first_name") ||
    lower.endsWith(".middle_name") ||
    lower.endsWith(".last_name")
  );
}

function valueAt(source, key) {
  if (!source || typeof source !== "object" || Array.isArray(source) || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, key)) return source[key];
  const lower = String(key).toLowerCase();
  const match = Object.keys(source).find((existing) => existing.toLowerCase() === lower);
  return match ? source[match] : undefined;
}

function composedPersonName(row, read) {
  const sources = [row];
  ["person", "org_persons", "organization_person", "employee", "intern"].forEach((key) => {
    const nested = valueAt(row, key);
    if (nested && typeof nested === "object") {
      sources.push(nested);
      const person = valueAt(nested, "person");
      if (person && typeof person === "object") sources.push(person);
    }
  });

  for (const source of sources) {
    for (const keys of NAME_PART_GROUPS) {
      const parts = keys
        .map((key) => plainText(valueAt(source, key.split(".").pop()) ?? (read ? read(key) : undefined)))
        .filter(Boolean);
      if (parts.length) return parts.join(" ");
    }
    for (const key of [
      "name",
      "full_name",
      "employee_name",
      "person_name",
      "person_full_name",
      "intern_name",
      "application_user_full_name",
    ]) {
      const text = plainText(valueAt(source, key));
      if (text) return text;
    }
  }
  return "";
}

function initialsFrom(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter((part) => part && part !== "—" && part !== "-");
  if (!parts.length) return "?";
  const letters = (parts[0][0] || "") + (parts[1]?.[0] || "");
  return letters.toUpperCase();
}

function relativeTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  if (diff < 0) return "";
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

function statusDotColor(row, columns, getNestedValue) {
  const statusCol = (columns || []).find((col) => isStatusColumnKey(col?.key));
  if (statusCol) {
    const tone = getStatusPillTone(getNestedValue(row, statusCol.key));
    if (tone?.dotColor) return tone.dotColor;
  }
  const active = row?.is_active;
  if (active === true || active === 1 || active === "1" || active === "Active") {
    return "var(--ems-green)";
  }
  if (active === false || active === 0 || active === "0" || active === "Inactive") {
    return "var(--ems-red)";
  }
  return null;
}

function pickIdentity(columns, row, getNestedValue, tableName, showNameAvatar) {
  const visible = (columns || []).filter((col) => col?.key && col.visible !== false);
  const titleCol =
    visible.find((col) => isNameAvatarColumn(col.key, tableName, showNameAvatar)) ||
    visible.find((col) => {
      const key = String(col.key);
      if (IMAGE_KEY.test(key) || isStatusColumnKey(key)) return false;
      return key === "name" || key === "title" || key === "full_name" || /_name$/.test(key);
    }) ||
    visible.find((col) => !IMAGE_KEY.test(col.key) && !isStatusColumnKey(col.key)) ||
    visible[0];

  const subtitleCol = SUBTITLE_RULES.reduce((found, rule) => {
    if (found) return found;
    return (
      visible.find((col) => {
        if (!col || col === titleCol || IMAGE_KEY.test(col.key)) return false;
        if (!rule.test(String(col.key))) return false;
        const text = plainText(getNestedValue(row, col.key));
        return text.length > 0 && text.length <= 80;
      }) || null
    );
  }, null);

  const hidden = new Set([titleCol?.key, subtitleCol?.key].filter(Boolean));
  const bodyCols = visible.filter((col) => {
    if (!col?.key || hidden.has(col.key) || IMAGE_KEY.test(col.key)) return false;
    const raw = getNestedValue(row, col.key);
    if (raw && typeof raw === "object" && !Array.isArray(raw)) return false;
    return true;
  });

  const tagCols = bodyCols.filter((col) => TAG_KEY.test(String(col.key)));
  const detailCols = bodyCols.filter((col) => !TAG_KEY.test(String(col.key)));

  return { titleCol, subtitleCol, detailCols, tagCols };
}

function CardFieldValue({ col, row, getNestedValue, renderCell }) {
  const raw = getNestedValue(row, col.key);

  if (isStatusColumnKey(col.key) || isPriorityColumnKey(col.key)) {
    const tone = isPriorityColumnKey(col.key)
      ? getPriorityPillTone(raw)
      : getStatusPillTone(raw);
    if (!tone) return <span className="ems-mcard-empty">—</span>;
    return (
      <span className="ems-mcard-status" style={{ color: tone.textColor }}>
        {tone.label}
      </span>
    );
  }

  const rendered = renderCell ? renderCell(row, col) : formatCellDisplay(raw);
  if (typeof rendered === "string" || typeof rendered === "number") {
    const text = String(rendered);
    if (text.includes("@") && !text.includes(" ")) {
      return (
        <a
          className="ems-mcard-link"
          href={`mailto:${text}`}
          onClick={(event) => event.stopPropagation()}
        >
          {text}
        </a>
      );
    }
    if (!text || text === "—" || text === "-") {
      return <span className="ems-mcard-empty">—</span>;
    }
  }
  return rendered;
}

function tagLabels(row, col, getNestedValue) {
  const raw = getNestedValue(row, col.key);
  if (Array.isArray(raw)) {
    return raw.map((item) => plainText(item)).filter(Boolean);
  }
  const text = plainText(raw);
  if (!text) return [];
  return text.split(",").map((part) => part.trim()).filter(Boolean);
}

export default function ListRecordCard({
  row,
  columns,
  getNestedValue,
  renderCell,
  tableName,
  showNameAvatar = true,
  onOpen,
  actions,
}) {
  // Callers use either read("field") or read(row, "field"). The table helper
  // splits the field path, so a row object must never be passed through as one.
  const read = (objOrPath, maybePath) => {
    const rawPath = arguments.length > 1 ? maybePath : objOrPath;
    if (typeof rawPath !== "string" || !rawPath) return undefined;
    if (typeof getNestedValue === "function") return getNestedValue(row, rawPath);
    return rawPath.split(".").reduce((acc, part) => (acc == null ? acc : acc[part]), row);
  };
  const { titleCol, subtitleCol, detailCols, tagCols } = pickIdentity(
    columns,
    row,
    read,
    tableName,
    showNameAvatar
  );

  const composedName = composedPersonName(row, read);
  const columnTitle = plainText(titleCol ? read(titleCol.key) : "");
  const title = composedName || columnTitle || "—";
  const designation = ["designation_name", "designation", "job_title", "position_name"]
    .map((key) => plainText(valueAt(row, key)) || plainText(read(key)))
    .find(Boolean) || "";
  const exitReason = ["exit_reason", "employment_exit_reason_name", "reason_for_exit"]
    .map((key) => plainText(valueAt(row, key)) || plainText(read(key)))
    .find(Boolean) || "";
  const employeeCode = ["employee_code", "person_code", "intern_code"]
    .map((key) => plainText(valueAt(row, key)) || plainText(read(key)))
    .find(Boolean) || "";
  const subtitleFromColumn = subtitleCol ? plainText(read(subtitleCol.key)) : "";
  const subtitle =
    employeeCode ||
    (subtitleFromColumn === designation ? "" : subtitleFromColumn);
  const detailFields = (composedName
    ? detailCols.filter((col) => !isNamePartKey(col.key))
    : detailCols
  ).filter((col) => !/designation|job_title|position_name|exit_reason|reason_for_exit|employee_code|person_code|intern_code/i.test(String(col.key)));
  const dot = statusDotColor(row, columns, read);
  const hasPhoto = Boolean(resolveRowProfileImageUrl(row, read));
  const usePersonAvatar =
    hasPhoto ||
    (titleCol && isNameAvatarColumn(titleCol.key, tableName, showNameAvatar));

  const agoSource = TIME_KEYS.map((key) => row?.[key]).find((value) => plainText(value) || value);
  const ago = agoSource ? relativeTime(agoSource) : "";

  const chips = tagCols.flatMap((col) => tagLabels(row, col, read));

  return (
    <article className="ems-mcard">
      <div className="ems-mcard-head">
        {usePersonAvatar ? (
          <ListPersonAvatar row={row} getNestedValue={read} size={42} />
        ) : (
          <div className="ems-mcard-initials" aria-hidden>
            {initialsFrom(title)}
          </div>
        )}
        <div className="ems-mcard-titles">
          {onOpen ? (
            <button type="button" className="ems-mcard-title" onClick={onOpen}>
              {title}
            </button>
          ) : (
            <div className="ems-mcard-title">{title}</div>
          )}
          {subtitle ? <div className="ems-mcard-sub">{subtitle}</div> : null}
        </div>
        {dot ? <span className="ems-mcard-dot" style={{ background: dot }} /> : null}
      </div>

      {designation || exitReason || detailFields.length > 0 ? (
        <div className="ems-mcard-fields">
          {designation ? (
            <div className="ems-mcard-row" key="designation">
              <span className="ems-mcard-label">Designation</span>
              <span className="ems-mcard-value">{designation}</span>
            </div>
          ) : null}
          {exitReason ? (
            <div className="ems-mcard-row" key="exit-reason">
              <span className="ems-mcard-label">Exit reason</span>
              <span className="ems-mcard-value">{exitReason}</span>
            </div>
          ) : null}
          {detailFields.map((col) => (
            <div className="ems-mcard-row" key={col.key}>
              <span className="ems-mcard-label">
                {String(col.label || col.key).replace(/_/g, " ")}
              </span>
              <span className="ems-mcard-value">
                <CardFieldValue
                  col={col}
                  row={row}
                  getNestedValue={read}
                  renderCell={renderCell}
                />
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {chips.length > 0 || ago ? (
        <div className="ems-mcard-meta">
          <div className="ems-mcard-chips">
            {chips.map((chip, index) => (
              <span className="ems-mcard-chip" key={`${chip}-${index}`}>
                {chip}
              </span>
            ))}
          </div>
          {ago ? (
            <span className="ems-mcard-ago">
              <Clock size={13} strokeWidth={2} />
              {ago}
            </span>
          ) : null}
        </div>
      ) : null}

      {actions ? <div className="ems-mcard-foot"><div className="ems-mcard-actions">{actions}</div></div> : null}
    </article>
  );
}
